import { useState, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { Loader2 } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { PredictionCard } from "@/components/predictions/PredictionCard";
import { PredictionDetailModal } from "@/components/predictions/PredictionDetailModal";
import { DataPagination } from "@/components/shared/DataPagination";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { CrossSportLinks } from "@/components/seo/CrossSportLinks";
import { SportGraphLinks } from "@/components/seo/SportGraphLinks";

import { useClusterPredictions, type ClusterKind } from "@/hooks/useClusterPredictions";
import { useLocalizedFields, useLocalizedList } from "@/hooks/useLocalizedContent";
import { useSEO } from "@/lib/useSEO";
import type { Prediction } from "@/types";
import { SITE_URL } from "@/lib/entities";
import { clusterCollectionJsonLd } from "@/lib/seoSchema";
import {
  clusterFaqs,
  clusterFaqJsonLd,
  clusterLeagueJsonLd,
  type ClusterFaq,
} from "@/lib/clusterFaqs";




interface Breadcrumb {
  name: string;
  path: string;
}

interface ClusterHubProps {
  kind: ClusterKind;
  slug: string;
  matchers: string[];
  heading: string;
  title: string;
  description: string;
  /** Short, unique social headline. Defaults to the hub heading. */
  ogTitle?: string;
  /** Short, unique social summary. Defaults to the meta description. */
  ogDescription?: string;
  intro: string;
  canonicalPath: string;
  /** Extra schema.org objects merged alongside the CollectionPage schema. */
  extraJsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Breadcrumb trail rendered visually and as BreadcrumbList JSON-LD. */
  breadcrumbs?: Breadcrumb[];
  keywords?: string;
  /** Social preview image (absolute or root-relative). Defaults to /og-image.jpg. */
  image?: string;
  /**
   * On-page FAQ + FAQPage JSON-LD. Omit to use the shared per-hub default set
   * (`clusterFaqs`), so EVERY filtered hub — not just Champions League — emits
   * valid, content-backed FAQ rich-result markup.
   */
  faqs?: ClusterFaq[];

}


/**
 * Shared layout for league / market / team / competition / club / country
 * prediction hubs: SEO head + single H1 + intro copy + paginated prediction
 * list + internal links + optional breadcrumb + structured data.
 */
export function ClusterHub({
  kind,
  slug,
  matchers,
  heading,
  title,
  description,
  ogTitle,
  ogDescription,
  intro,
  canonicalPath,
  extraJsonLd,
  breadcrumbs,
  keywords,
  image,
  faqs,

}: ClusterHubProps) {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [selected, setSelected] = useState<Prediction | null>(null);
  const [modalOpen, setModalOpen] = useState(false);


  const { data, isLoading, isFetching } = useClusterPredictions({
    kind,
    matchers,
    page,
    pageSize,
  });

  const rows = data?.rows ?? [];
  const totalCount = data?.count ?? 0;

  // Complete CollectionPage + ItemList of SportsEvents (location, organizer,
  // dates, offers…) via the shared builder, so runtime and prerender match.
  const collectionSchema: Record<string, unknown> = clusterCollectionJsonLd({
    name: heading,
    description,
    path: canonicalPath,
    fixtures: rows.map((p) => ({
      id: p.id,
      match_id: p.match_id,
      team_home: p.team_home,
      team_away: p.team_away,
      league: p.league,
      kickoff_time: p.kickoff_time,
      odds: p.odds,
      market_type: p.market_type,
      predicted_outcome: p.predicted_outcome,
    })),
  }) as unknown as Record<string, unknown>;

  const breadcrumbSchema: Record<string, unknown> | null = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      }
    : null;

  const extras: Record<string, unknown>[] = extraJsonLd
    ? Array.isArray(extraJsonLd)
      ? extraJsonLd
      : [extraJsonLd]
    : [];
  const hasType = (t: string) =>
    extras.some((n) => {
      const ty = (n as { "@type"?: unknown })["@type"];
      return Array.isArray(ty) ? ty.includes(t) : ty === t;
    });

  // FAQ: an explicit `faqs` prop wins, otherwise the shared default set. When a
  // hub already ships its own FAQPage node via `extraJsonLd` (e.g. the Champions
  // League hub) we render that node's questions on-page instead of adding a
  // second FAQPage — duplicate FAQPage nodes are a rich-result validation error.
  const faqEntries: ClusterFaq[] = faqs ?? clusterFaqs({ kind, heading });
  const ownFaqPage = hasType("FAQPage");
  const visibleFaqs: ClusterFaq[] = ownFaqPage
    ? (() => {
        const node = extras.find((n) => {
          const ty = (n as { "@type"?: unknown })["@type"];
          return Array.isArray(ty) ? ty.includes("FAQPage") : ty === "FAQPage";
        }) as { mainEntity?: unknown } | undefined;
        const list = Array.isArray(node?.mainEntity) ? node!.mainEntity : [];
        return list
          .map((q) => {
            const item = q as { name?: string; acceptedAnswer?: { text?: string } };
            return { question: item.name ?? "", answer: item.acceptedAnswer?.text ?? "" };
          })
          .filter((q) => q.question && q.answer);
      })()
    : faqEntries;

  // League hubs also declare the governing league as a SportsOrganization so
  // every league-filtered section carries league schema too.
  const leagueSchema: Record<string, unknown> | null =
    kind === "league" && !hasType("SportsOrganization")
      ? clusterLeagueJsonLd({ heading, canonicalPath, siteUrl: SITE_URL })
      : null;

  const jsonLd: Record<string, unknown>[] = [
    collectionSchema,
    ...(breadcrumbSchema ? [breadcrumbSchema] : []),
    ...(leagueSchema ? [leagueSchema] : []),
    ...(ownFaqPage || visibleFaqs.length === 0 ? [] : [clusterFaqJsonLd(visibleFaqs)]),

    ...extras,
  ];


  useSEO({
    title,
    description,
    // Social copy stays short and league-specific so each filtered hub gets a
    // distinct preview instead of a truncated SERP title.
    ogTitle: ogTitle ?? heading,
    ogDescription:
      ogDescription ?? (description.length > 90 ? `${description.slice(0, 87).trimEnd()}…` : description),
    canonicalPath,
    keywords: keywords!,
    image: image!,
    jsonLd,
  });




  // Registry prose (heading + intro) and the FAQ set are English source text.
  // Route them through the translation cache so non-English hubs render fully
  // localized copy instead of English page bodies.
  const entityId = `${kind}:${slug}`;
  const proseFields = useMemo(() => ({ heading, intro }), [heading, intro]);
  const { values: prose } = useLocalizedFields("cluster_hub", entityId, proseFields);

  const faqItems = useMemo(
    () =>
      visibleFaqs.flatMap((f, i) => [
        { entityType: "cluster_faq", entityId: `${entityId}#${i}`, field: "question", sourceText: f.question },
        { entityType: "cluster_faq", entityId: `${entityId}#${i}`, field: "answer", sourceText: f.answer },
      ]),
    [visibleFaqs, entityId],
  );
  const { map: faqMap } = useLocalizedList(faqItems);

  const localizedHeading = prose["heading"] || heading;

  return (
    <div className="space-y-8 animate-fade-in">
      {breadcrumbs?.length ? (
        <Breadcrumbs items={breadcrumbs} className="text-sm text-muted-foreground" />
      ) : null}
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold">{localizedHeading}</h1>
        <p className="text-muted-foreground max-w-3xl">{prose["intro"] || intro}</p>
      </header>


      <section aria-label={t("cluster.predictionsLabel")} className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t("cluster.loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
            {t("cluster.empty")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((p) => (
                <PredictionCard
                  key={p.id}
                  prediction={p}
                  onClick={() => {
                    setSelected(p);
                    setModalOpen(true);
                  }}
                />
              ))}
            </div>
            <DataPagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[12, 24, 48]}
              isLoading={isFetching}
            />
          </>
        )}
      </section>

      {visibleFaqs.length > 0 ? (
        <section aria-labelledby="cluster-faq-heading" className="space-y-4">
          <h2 id="cluster-faq-heading" className="text-xl font-semibold">
            {t("cluster.faqHeading", { name: localizedHeading })}
          </h2>
          <dl className="space-y-4">
            {visibleFaqs.map((f, i) => (
              <div key={f.question} className="rounded-lg border border-border p-4">
                <dt className="font-medium">
                  <h3 className="text-base font-medium">
                    {faqMap.get(`${entityId}#${i}::question`) ?? f.question}
                  </h3>
                </dt>
                <dd className="mt-2 text-sm text-muted-foreground">
                  {faqMap.get(`${entityId}#${i}::answer`) ?? f.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}



      <p className="text-sm text-muted-foreground">
        <Trans
          i18nKey="cluster.crossLinks"
          components={{
            today: <Link to="/predictions/today" className="text-primary hover:underline" />,
            all: <Link to="/predictions" className="text-primary hover:underline" />,
            high: <Link to="/high-odds" className="text-primary hover:underline" />,
            safe: <Link to="/safe-bets" className="text-primary hover:underline" />,
          }}
        />
      </p>


      <InternalLinks excludeSlug={slug} />

      <SportGraphLinks />
      <CrossSportLinks />


      <PredictionDetailModal prediction={selected} open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
